// src/utils/corpusProjection.js
//
// Slim row projection for POST /proxy/corpus/compare (docs/har-corpus-spec.md
// §3 "Data-size + memory budget" + §8). The server takes a SLIM projection,
// never the dashboard's full rows: `{url, requestType, status, issues,
// validData: {tables: {bids}, adInspector, correlationKeys}, requestBodyParsed}`
// — and `requestBodyParsed` only on bid-request-type rows (eids/consent live
// there); `responseBody`, headers, device identifiers, and everything else
// are stripped client-side before the row ever leaves the browser. This
// module is the ONE place that builds that projection so the shape can't
// drift from what the server expects — every call site (TrendsPage.jsx today,
// anything else later) imports from here rather than hand-rolling its own
// slice of a row.
//
// Pure functions, no React/fetch/Date.now() — unit-tested directly
// (corpusProjection.test.js).

/**
 * Request types whose body carries the bid-request-side signal the compare
 * endpoint's advisories care about (eids, consent, schain, bidfloor) — see
 * spec §3. Everything else's requestBodyParsed is stripped even if present
 * on the source row.
 */
export const BID_REQUEST_TYPES = ['prebid', 'sdk_bidding', 'amazon'];

/**
 * Client-side soft cap on the projection's serialized size (~4MB, spec §3;
 * the server enforces a hard 5MB/413 limit — this cap leaves headroom and
 * degrades gracefully client-side instead of relying on the server reject).
 * Size is approximated via JSON.stringify(...).length (UTF-16 code units,
 * not exact UTF-8 bytes) — close enough for a soft client budget; the
 * server's own byte-accurate check is the real gate.
 */
export const MAX_PROJECTION_BYTES = 4 * 1024 * 1024;

function safeParseJson(v) {
  if (v == null) return null;
  if (typeof v === 'object') return v;
  if (typeof v !== 'string') return null;
  try { return JSON.parse(v); } catch { return null; }
}

function approxBytes(value) {
  try { return JSON.stringify(value).length; } catch { return 0; }
}

/**
 * projectRow(row) -> the slim shape for one row, or null when the row is
 * malformed (not an object, or carries no usable url/requestType) — callers
 * skip nulls rather than crash on a bad row (proxy validator convention:
 * never throw, degrade).
 */
export function projectRow(row) {
  if (!row || typeof row !== 'object') return null;

  const url = typeof row.url === 'string' ? row.url : '';
  const requestType = typeof row.requestType === 'string'
    ? row.requestType
    : (typeof row.type === 'string' ? row.type : '');
  if (!url && !requestType) return null; // nothing identifiable to project

  const status = row.status != null ? row.status : (row.responseCode != null ? row.responseCode : null);
  const issues = Array.isArray(row.issues) ? row.issues : [];

  const srcValidData = row.validData && typeof row.validData === 'object' ? row.validData : {};
  const bids = srcValidData.tables && Array.isArray(srcValidData.tables.bids)
    ? srcValidData.tables.bids
    : undefined;

  const validData = {};
  if (bids) validData.tables = { bids };
  if (srcValidData.adInspector != null) validData.adInspector = srcValidData.adInspector;
  if (srcValidData.correlationKeys != null) validData.correlationKeys = srcValidData.correlationKeys;

  const projected = { url, requestType, status, issues };
  if (Object.keys(validData).length > 0) projected.validData = validData;

  // requestBodyParsed ONLY on bid-request-type rows — everything else
  // (responseBody, creative payloads, non-bid request bodies) is dropped.
  if (BID_REQUEST_TYPES.includes(requestType)) {
    const body = row.requestBodyParsed != null ? row.requestBodyParsed : safeParseJson(row.requestBody);
    if (body != null) projected.requestBodyParsed = body;
  }

  return projected;
}

/**
 * buildCorpusProjection(rows, opts?) -> { rows, truncated, originalCount,
 * projectedCount, droppedCount }.
 *
 * Projects every row via projectRow() (skipping malformed ones), then
 * enforces the ~4MB soft cap: non-bid rows are dropped first (oldest first),
 * and only if that alone isn't enough are bid rows dropped too (also oldest
 * first) — bid rows carry the eids/consent signal advisories need, so they're
 * the last thing sacrificed. `truncated` is set whenever ANY row had to be
 * dropped to fit the budget.
 *
 * "Oldest first" assumes the input array is newest-first — the convention
 * TrafficContext.js's groupedResults uses (`[newEntry, ...group]`) — so
 * "oldest" is the tail of the array. Order of the KEPT rows in the output is
 * preserved from the input.
 *
 * opts.maxBytes overrides MAX_PROJECTION_BYTES (test seam).
 */
export function buildCorpusProjection(rows, opts = {}) {
  const maxBytes = Number.isFinite(opts.maxBytes) ? opts.maxBytes : MAX_PROJECTION_BYTES;
  const source = Array.isArray(rows) ? rows : [];

  const entries = [];
  for (const r of source) {
    const p = projectRow(r);
    if (!p) continue;
    entries.push({ row: p, isBid: p.requestBodyParsed != null, bytes: approxBytes(p) });
  }

  const originalCount = entries.length;
  let totalBytes = entries.reduce((s, e) => s + e.bytes, 0);
  const kept = entries.map(() => true);
  let truncated = false;

  if (totalBytes > maxBytes) {
    truncated = true;

    // Pass 1 — drop non-bid rows, oldest first (from the tail).
    for (let i = entries.length - 1; i >= 0 && totalBytes > maxBytes; i--) {
      if (kept[i] && !entries[i].isBid) {
        kept[i] = false;
        totalBytes -= entries[i].bytes;
      }
    }
    // Pass 2 — still over budget: drop oldest bid rows too (from the tail).
    for (let i = entries.length - 1; i >= 0 && totalBytes > maxBytes; i--) {
      if (kept[i]) {
        kept[i] = false;
        totalBytes -= entries[i].bytes;
      }
    }
  }

  const projected = entries.filter((_, i) => kept[i]).map((e) => e.row);

  return {
    rows: projected,
    truncated,
    originalCount,
    projectedCount: projected.length,
    droppedCount: originalCount - projected.length,
  };
}

export default buildCorpusProjection;
